import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Eye, EyeOff, Key, Lock, Unlock, User, LogOut, Shield, ChevronRight, ChevronDown, Check, X } from 'lucide-react';
import { spacing, fontSize, fontWeight, borderRadius } from '../../styles/theme';
import { webvonkClient } from '../../lib/supabase';
import { usePinLock } from '../../hooks/usePinLock';
import { useSession } from '../../contexts/SessionContext';
import formatUsername from '../lib/formatUsername';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} 0.15s ease;
`;

const Dialog = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 18px;
  padding: 28px 24px 20px;
  width: 320px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  animation: ${scaleIn} 0.18s ease;
`;

const DialogIcon = styled.div`
  width: 48px; height: 48px;
  border-radius: 50%;
  background: rgba(239,68,68,0.1);
  color: #ef4444;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px;
`;

const DialogTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  text-align: center;
  margin-bottom: 8px;
`;

const DialogSub = styled.div`
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
  text-align: center;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const DialogActions = styled.div`
  display: flex;
  gap: 10px;
`;

const DialogBtn = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${p => p.$danger ? 'transparent' : p.theme.colors.border};
  background: ${p => p.$danger ? '#ef4444' : 'transparent'};
  color: ${p => p.$danger ? '#fff' : p.theme.colors.text};
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.85; }
`;

const Container = styled.div`
  height: calc(100vh - 52px);
  background: ${p => p.theme.colors.background};
  overflow: hidden;
`;

const Content = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 24px 28px 48px;
  max-width: 560px;
  animation: ${fadeUp} 0.3s ease;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin: 0 0 24px;
`;

/* ── Profile card ── */
const ProfileCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${p => p.theme.colors.brand};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
`;

const ProfileInfo = styled.div`flex: 1;`;

const ProfileName = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin-bottom: 3px;
`;

const ProfileRole = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${p => p.theme.colors.muted};
`;

const ProfileUsername = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
  opacity: 0.6;
`;

/* ── Section ── */
const Section = styled.div`
  margin-bottom: 16px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: ${p => p.theme.colors.muted};
  margin-bottom: 8px;
  padding-left: 4px;
`;

const Card = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
  overflow: hidden;
`;

/* ── Row ── */
const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  background: none;
  border: none;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  cursor: ${p => p.$static ? 'default' : 'pointer'};
  text-align: left;
  font-family: inherit;
  transition: background 0.12s;
  &:last-child { border-bottom: none; }
  &:hover:not([disabled]) { background: ${p => p.$static ? 'none' : (p.theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)')}; }
`;

const RowIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${p => p.$brand ? p.theme.colors.brand + '18' : p.$color + '18'};
  color: ${p => p.$brand ? p.theme.colors.brand : p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RowContent = styled.div`flex: 1; min-width: 0;`;

const RowTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.$danger ? '#ef4444' : p.theme.colors.text};
`;

const RowSub = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  margin-top: 1px;
`;

const RowRight = styled.div`
  display: flex;
  align-items: center;
  color: ${p => p.theme.colors.muted};
`;

/* ── Expandable wachtwoord sectie ── */
const ExpandBody = styled.div`
  padding: 16px;
  border-top: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'};
`;

const FieldGroup = styled.div`margin-bottom: 14px;`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  margin-bottom: 6px;
`;

const FieldWrap = styled.div`position: relative; display: flex; align-items: center;`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 40px 10px 12px;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.$error ? 'rgba(239,68,68,0.5)' : p.$success ? 'rgba(34,197,94,0.4)' : p.theme.colors.border};
  border-radius: 10px;
  color: ${p => p.theme.colors.text};
  font-size: 14px;
  font-family: inherit;
  &:focus { outline: none; border-color: ${p => p.$error ? 'rgba(239,68,68,0.6)' : p.theme.colors.brand + '80'}; }
`;

const EyeBtn = styled.button`
  position: absolute; right: 10px;
  background: none; border: none; cursor: pointer;
  color: ${p => p.theme.colors.muted}; opacity: 0.5; padding: 0;
  display: flex; align-items: center;
  &:hover { opacity: 0.9; }
`;

const StrengthBars = styled.div`display: flex; gap: 3px; margin: 8px 0 4px;`;
const StrengthBar = styled.div`
  flex: 1; height: 3px; border-radius: 2px;
  background: ${p => p.$filled ? p.$color : p.theme.colors.border};
  transition: background 0.2s;
`;
const StrengthLabel = styled.div`font-size: 11px; color: ${p => p.$color}; font-weight: 600;`;
const ErrorItem = styled.div`font-size: 11px; color: rgba(239,68,68,0.8); margin-top: 2px;`;
const MatchMsg = styled.div`
  margin-top: 4px; font-size: 11px;
  color: ${p => p.$ok ? 'rgba(34,197,94,0.9)' : 'rgba(239,68,68,0.8)'};
`;

const SaveBtn = styled.button`
  width: 100%;
  padding: 11px;
  border-radius: 10px;
  border: none;
  background: ${p => p.disabled ? p.theme.colors.border : p.theme.colors.brand};
  color: ${p => p.disabled ? p.theme.colors.muted : '#fff'};
  font-size: 14px;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  margin-top: 4px;
  font-family: inherit;
  transition: background 0.15s;
  &:hover:not(:disabled) { opacity: 0.88; }
`;

const FeedbackMsg = styled.div`
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  background: ${p => p.$success ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)'};
  color: ${p => p.$success ? '#22c55e' : '#ef4444'};
  border: 1px solid ${p => p.$success ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'};
`;

/* ── PIN dots ── */
const PinDots = styled.div`display: flex; gap: 10px; justify-content: center; margin: 16px 0 8px;`;
const PinDot = styled.div`
  width: 12px; height: 12px; border-radius: 50%;
  background: ${p => p.$filled ? p.theme.colors.brand : p.theme.colors.border};
  transition: background 0.15s;
`;
const PinGrid = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; max-width: 220px; margin: 0 auto;`;
const PinKey = styled.button`
  padding: 14px;
  border-radius: 12px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background};
  font-size: 18px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  cursor: pointer;
  font-family: inherit;
  transition: background 0.1s;
  &:hover { background: ${p => p.theme.colors.border}; }
`;
const PinLabel = styled.div`font-size: 13px; font-weight: 600; color: ${p => p.theme.colors.text}; text-align: center; margin-bottom: 12px;`;

// ── Helpers ──
const COMMON = ['123456','password','123456789','12345678','12345','qwerty','abc123','111111','admin','letmein'];
function getStrength(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[!@#$%^&*]/.test(pw)) s++;
  return s;
}
function getErrors(pw, current, username) {
  const errs = [];
  if (pw.length < 8) errs.push('Minimaal 8 tekens');
  if (pw.includes(' ')) errs.push('Geen spaties');
  if (!/[A-Z]/.test(pw)) errs.push('Minimaal 1 hoofdletter');
  if (!/[a-z]/.test(pw)) errs.push('Minimaal 1 kleine letter');
  if (!/[0-9]/.test(pw)) errs.push('Minimaal 1 cijfer');
  if (!/[!@#$%^&*]/.test(pw)) errs.push('Minimaal 1 speciaal teken');
  if (pw === current) errs.push('Niet gelijk aan huidig wachtwoord');
  if (username && pw.toLowerCase() === username.toLowerCase()) errs.push('Niet gelijk aan gebruikersnaam');
  if (COMMON.includes(pw.toLowerCase())) errs.push('Geen veelgebruikt wachtwoord');
  return errs;
}
const STRENGTH_COLORS = ['#ef4444','#ef4444','#f97316','#eab308','#22c55e'];
const STRENGTH_LABELS = ['','Zwak','Zwak','Gemiddeld','Sterk','Zeer sterk'];

export default function AccountSettingsScreen() {
  const navigate = useNavigate();
  const { logout } = useSession();

  const stored = (() => { try { return JSON.parse(localStorage.getItem('kmfc_user') || '{}'); } catch { return {}; } })();
  const rawUsername = stored.username || '';
  const displayName = formatUsername(rawUsername);
  const role = stored.role?.name || 'Medewerker';
  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const isKmfcUser = /^kmfc\d+$/i.test(rawUsername);

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Wachtwoord
  const [pwOpen, setPwOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwFeedback, setPwFeedback] = useState(null);

  const errors = newPw ? getErrors(newPw, current, rawUsername) : [];
  const strength = getStrength(newPw);
  const color = STRENGTH_COLORS[strength] ?? '#22c55e';
  const isValid = current && !errors.length && confirm === newPw && newPw !== current;

  const handlePwSubmit = async () => {
    if (!isValid || pwLoading) return;
    setPwLoading(true);
    setPwFeedback(null);
    try {
      const email = `${rawUsername}@webvonk.app`;
      const { error: signInError } = await webvonkClient.auth.signInWithPassword({ email, password: current });
      if (signInError) { setPwFeedback({ msg: 'Huidig wachtwoord onjuist.', success: false }); return; }
      const { error: updateError } = await webvonkClient.auth.updateUser({ password: newPw });
      if (updateError) throw updateError;
      setPwFeedback({ msg: 'Wachtwoord succesvol gewijzigd.', success: true });
      setCurrent(''); setNewPw(''); setConfirm('');
      setTimeout(() => { setPwOpen(false); setPwFeedback(null); }, 2000);
    } catch {
      setPwFeedback({ msg: 'Er is iets misgegaan.', success: false });
    } finally {
      setPwLoading(false);
    }
  };

  // PIN
  const { hasPin, setPin, removePin } = usePinLock();
  const [pinOpen, setPinOpen] = useState(false);
  const [pinStep, setPinStep] = useState('enter');
  const [pin1, setPin1] = useState('');
  const [pin2, setPin2] = useState('');
  const [pinFeedback, setPinFeedback] = useState(null);

  const handlePinDigit = (digit) => {
    if (pinStep === 'enter' && pin1.length < 6) {
      const next = pin1 + digit;
      setPin1(next);
      if (next.length === 6) setPinStep('confirm');
    } else if (pinStep === 'confirm' && pin2.length < 6) {
      const next = pin2 + digit;
      setPin2(next);
      if (next.length === 6) confirmPin(pin1, next);
    }
  };

  const confirmPin = async (p1, p2) => {
    if (p1 !== p2) {
      setPinFeedback({ msg: 'Pincodes komen niet overeen', success: false });
      setPin1(''); setPin2(''); setPinStep('enter');
      return;
    }
    const ok = await setPin(p1);
    if (ok) {
      setPinFeedback({ msg: 'Pincode ingesteld', success: true });
      setPin1(''); setPin2('');
      setTimeout(() => { setPinOpen(false); setPinFeedback(null); }, 1500);
    } else {
      setPinFeedback({ msg: 'Er ging iets mis', success: false });
      setPinStep('enter'); setPin1(''); setPin2('');
    }
  };

  const handleRemovePin = async () => {
    const ok = await removePin();
    if (ok) setPinFeedback({ msg: 'Pincode verwijderd', success: true });
    setTimeout(() => setPinFeedback(null), 2000);
  };

  const currentPinValue = pinStep === 'enter' ? pin1 : pin2;

  return (
    <>
    <Container>
      <Content>
        <PageTitle>Account</PageTitle>

        {/* Profiel */}
        <ProfileCard>
          <Avatar style={isKmfcUser ? { background: '#1a1a1a', padding: 4 } : {}}>
            {isKmfcUser
              ? <img src={`${process.env.PUBLIC_URL}/assets/KMFC_logo.png`} alt="KMFC" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }} />
              : initials}
          </Avatar>
          <ProfileInfo>
            <ProfileName>{displayName}</ProfileName>
            <ProfileRole>{role}</ProfileRole>
            <ProfileUsername>@{rawUsername}</ProfileUsername>
          </ProfileInfo>
        </ProfileCard>

        {/* Beveiliging */}
        <Section>
          <SectionLabel>Beveiliging</SectionLabel>
          <Card>
            {/* Wachtwoord */}
            <Row onClick={() => { setPwOpen(v => !v); setPwFeedback(null); }}>
              <RowIcon $brand><Key size={16} /></RowIcon>
              <RowContent>
                <RowTitle>Wachtwoord wijzigen</RowTitle>
                <RowSub>Wijzig je inlogwachtwoord</RowSub>
              </RowContent>
              <RowRight>{pwOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</RowRight>
            </Row>

            {pwOpen && (
              <ExpandBody>
                <FieldGroup>
                  <FieldLabel>Huidig wachtwoord</FieldLabel>
                  <FieldWrap>
                    <Input type={showCurrent ? 'text' : 'password'} value={current} onChange={e => setCurrent(e.target.value)} autoComplete="current-password" />
                    <EyeBtn type="button" onClick={() => setShowCurrent(v => !v)} tabIndex={-1}>{showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}</EyeBtn>
                  </FieldWrap>
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>Nieuw wachtwoord</FieldLabel>
                  <FieldWrap>
                    <Input type={showNew ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} $error={newPw && errors.length > 0} $success={newPw && !errors.length} autoComplete="new-password" />
                    <EyeBtn type="button" onClick={() => setShowNew(v => !v)} tabIndex={-1}>{showNew ? <EyeOff size={15} /> : <Eye size={15} />}</EyeBtn>
                  </FieldWrap>
                  {newPw && (
                    <>
                      <StrengthBars>{[1,2,3,4].map(i => <StrengthBar key={i} $filled={strength >= i} $color={color} />)}</StrengthBars>
                      <StrengthLabel $color={color}>{STRENGTH_LABELS[strength]}</StrengthLabel>
                      {errors.map(e => <ErrorItem key={e}>· {e}</ErrorItem>)}
                    </>
                  )}
                </FieldGroup>
                <FieldGroup style={{ marginBottom: 0 }}>
                  <FieldLabel>Bevestig nieuw wachtwoord</FieldLabel>
                  <FieldWrap>
                    <Input type={showConfirm ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} $error={confirm && confirm !== newPw} $success={confirm && confirm === newPw} autoComplete="new-password" />
                    <EyeBtn type="button" onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>{showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}</EyeBtn>
                  </FieldWrap>
                  {confirm && confirm !== newPw && <MatchMsg $ok={false}>Wachtwoorden komen niet overeen</MatchMsg>}
                  {confirm && confirm === newPw && newPw && <MatchMsg $ok>Wachtwoorden komen overeen ✓</MatchMsg>}
                </FieldGroup>
                <SaveBtn onClick={handlePwSubmit} disabled={!isValid || pwLoading}>{pwLoading ? 'Bezig...' : 'Opslaan'}</SaveBtn>
                {pwFeedback && <FeedbackMsg $success={pwFeedback.success}>{pwFeedback.msg}</FeedbackMsg>}
              </ExpandBody>
            )}

            {/* PIN */}
            <Row onClick={() => { setPinOpen(v => !v); setPinFeedback(null); setPinStep('enter'); setPin1(''); setPin2(''); }}>
              <RowIcon $brand>{hasPin ? <Lock size={16} /> : <Unlock size={16} />}</RowIcon>
              <RowContent>
                <RowTitle>PIN-code</RowTitle>
                <RowSub>{hasPin ? 'Pincode is ingesteld' : 'Stel een pincode in voor snelle toegang'}</RowSub>
              </RowContent>
              <RowRight>{pinOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</RowRight>
            </Row>

            {pinOpen && (
              <ExpandBody>
                {hasPin && !pinStep ? null : (
                  <>
                    <PinLabel>{pinStep === 'enter' ? 'Voer een nieuwe pincode in' : 'Bevestig je pincode'}</PinLabel>
                    <PinDots>
                      {[0,1,2,3,4,5].map(i => <PinDot key={i} $filled={currentPinValue.length > i} />)}
                    </PinDots>
                    <PinGrid>
                      {[1,2,3,4,5,6,7,8,9].map(n => (
                        <PinKey key={n} onClick={() => handlePinDigit(String(n))}>{n}</PinKey>
                      ))}
                      <div />
                      <PinKey onClick={() => handlePinDigit('0')}>0</PinKey>
                      <PinKey onClick={() => {
                        if (pinStep === 'enter') setPin1(v => v.slice(0, -1));
                        else setPin2(v => v.slice(0, -1));
                      }}>⌫</PinKey>
                    </PinGrid>
                  </>
                )}
                {hasPin && (
                  <SaveBtn onClick={handleRemovePin} style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', marginTop: 12 }}>
                    Pincode verwijderen
                  </SaveBtn>
                )}
                {pinFeedback && <FeedbackMsg $success={pinFeedback.success} style={{ marginTop: 12 }}>{pinFeedback.msg}</FeedbackMsg>}
              </ExpandBody>
            )}
          </Card>
        </Section>

        {/* Sessie */}
        <Section>
          <SectionLabel>Sessie</SectionLabel>
          <Card>
            <Row onClick={() => setShowLogoutConfirm(true)}>
              <RowIcon $color="#ef4444"><LogOut size={16} /></RowIcon>
              <RowContent>
                <RowTitle $danger>Uitloggen</RowTitle>
                <RowSub>Beëindig je huidige sessie</RowSub>
              </RowContent>
              <RowRight><ChevronRight size={16} /></RowRight>
            </Row>
          </Card>
        </Section>
      </Content>
    </Container>

      {showLogoutConfirm && ReactDOM.createPortal(
        <Overlay onClick={() => setShowLogoutConfirm(false)}>
          <Dialog onClick={e => e.stopPropagation()}>
            <DialogIcon><LogOut size={22} /></DialogIcon>
            <DialogTitle>Uitloggen?</DialogTitle>
            <DialogSub>Je wordt uitgelogd uit je huidige sessie. Je kunt daarna opnieuw inloggen.</DialogSub>
            <DialogActions>
              <DialogBtn onClick={() => setShowLogoutConfirm(false)}>Annuleren</DialogBtn>
              <DialogBtn $danger onClick={() => { setShowLogoutConfirm(false); logout(); }}>Uitloggen</DialogBtn>
            </DialogActions>
          </Dialog>
        </Overlay>,
        document.body
      )}
    </>
  );
}
